import { useEffect, useState, createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Route, Routes, useLocation } from "react-router";
import { ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import useLocalStorage from "use-local-storage";
import Navbar from "./components/Navbar/Navbar";
import Footer from "./components/Footer/Footer";
import Home from "./pages/Home/Home";
import ScrollToTop from "./components/scrollToTop/ScrollToTop";
import AdvancedOrangeSpinner from "./components/LoadingSpinner/OrangeSpinner";

import ProductDetails from "./pages/ProductDetails/ProductDetails";

import Cart from "./components/cart/cart";
import Contact from "./components/contact/contact";
import Checkout from "./pages/checkout/checkout";
import Offers from "./pages/Offers/Offers";

import SubCategoriesPage from "./pages/Subcategories/SubCategories";
import CategoriesPage from "./pages/Categories/Categories";
import AllSubDepartments from "./pages/departments/AllSubDepartments";
import ChatBot from "./components/ChatBot/ChatBot";
import Register from "./pages/Register/Register";
import VerifyOTP from "./pages/VerifyOTP/VerifyOTP";
import Login from "./pages/Login/Login";
import ForgetPassword from "./pages/ForgetPassword/ForgetPassword";
import OTPForget from "./pages/OTPForget/OTPForget";
import ResetPassword from "./pages/ResetPassword/ResetPassword";
import Profile from "./pages/Profile/Profile";
import Orders from "./pages/Orders/Orders";
import Favorites from "./pages/Favorites/Favorites";
import Terms from "./pages/Terms/Terms";
import Privacy from "./pages/Privacy/Privacy";

// Dark Mode Context
const DarkModeContext = createContext();

export const useDarkMode = () => {
  const context = useContext(DarkModeContext);
  if (!context) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
};

// Dark Mode Provider Component
function DarkModeProvider({ children }) {
  const [isDarkMode, setIsDarkMode] = useLocalStorage('darkMode', false);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark-mode');
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark-mode');
      document.documentElement.setAttribute('data-theme', 'light');
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <DarkModeContext.Provider value={{ isDarkMode, toggleDarkMode }}>
      {children}
    </DarkModeContext.Provider>
  );
}

function LayoutWrapper({ children }) {
  const location = useLocation();

  const authRoutes = [
    "/createaccount",
    "/verify",
    "/verify-otp",
    "/login",
    "/forgot-password",
    "/otp-forget",
    "/reset-password",
    "/vertification",
    "/chooseaccounttype",
    "/mediadataregistration",
    "/active-account"
  ];

  const hideLayout = authRoutes.includes(location.pathname);

  return (
    <>
      {!hideLayout && <Navbar />}
      {children}
      {!hideLayout && <Footer />}
    </>
  );
}

function AppRoutes() {
  const { i18n } = useTranslation("global");
  useEffect(() => {
    document.dir = i18n.dir();
    document.title = "One Plus";
  }, [i18n.language]);
  return (
    <Routes>
      <Route path="/" element={<Home />} />

      <Route path="/productDetails/:id" element={<ProductDetails />} />

      <Route path="/cart" element={<Cart />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/offers" element={<Offers />} />
      <Route path="/departments/:id/:sub" element={<AllSubDepartments />} />

      <Route path="/categories" element={<CategoriesPage />} />
      <Route path="/categories/:id" element={<SubCategoriesPage />} />
      <Route path="/createaccount" element={<Register />} />
      <Route path="/verify" element={<VerifyOTP />} />
      <Route path="/verify-otp" element={<VerifyOTP />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgetPassword />} />
      <Route path="/otp-forget" element={<OTPForget />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/orders" element={<Orders />} />
      <Route path="/orders" element={<Orders />} />
      <Route path="/favorites" element={<Favorites />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
    </Routes>
  );
}
function App() {
  const [loading, setLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useLocalStorage('darkMode', false);

  useEffect(() => {
    // Initialize Session ID if not exists
    if (!sessionStorage.getItem('sessionId')) {
      const sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('sessionId', sessionId);
    }

    const timer = setTimeout(() => {
      setLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <DarkModeProvider>
      <BrowserRouter>
        {loading && <AdvancedOrangeSpinner />}

        <ToastContainer
          position="top-right"
          autoClose={2000}



          toastStyle={{
            background: isDarkMode ? 'var(--color-card)' : '#fff',
            color: isDarkMode ? 'var(--color-text)' : '#000',
            border: isDarkMode ? '1px solid var(--color-border)' : '1px solid #e0e0e0',
            boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.1)'
          }}
          progressStyle={{
            background: isDarkMode ? 'var(--color-primary)' : '#000c3d'
          }}
        />
        <LayoutWrapper>
          <AppRoutes />
        </LayoutWrapper>
        <ScrollToTop />
        <ChatBot />
      </BrowserRouter>
    </DarkModeProvider>
  );
}

export default App;
